import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Table } from 'lucide-react'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { AuthService } from '@/services/core/auth'
import { AdUnitsApi, AdUnit } from '@/services/adUnits/adUnitsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { AppShell } from '../../components/AppShell'
import { AdUnitTable } from '../../components/AdUnitTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { AdUnitTagDownloadDialog } from '../../components/AdUnitTagDownloadDialog'
import { StatusFilterButton } from '../../components/StatusFilter'
import { FilterField } from '@/components/FilterButton'
import { EarningsAccountSelect } from '@/components/EarningsAccountSelect'
import { SiteSelect } from '@/components/SiteSelect'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { ImportExportButtons } from '../../components/ImportExportButtons'
import { dimensionsTransformer } from '../../utils/importExport'
import { CardContent, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { useNetwork } from '@/contexts/NetworkContext'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function AdUnitsPage() {
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()
  const [search, setSearch] = useUrlSearch()
  const { scopeKey, scopeReady } = useNetwork()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.adUnits.status', 'ACTIVE')
  const [publisherFilter, setPublisherFilter] = usePersistedState<string>('filters.adUnits.publisher', '')
  const [siteFilter, setSiteFilter] = usePersistedState<string>('filters.adUnits.site', '')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const listFilters = {
    search,
    status: statusFilter,
    earningsAccountId: publisherFilter || undefined,
    siteId: siteFilter || undefined,
  }
  // Every filter is a request parameter, so the screen only ever holds the one
  // page it shows — the whole list is walked for a download and nothing else.
  const fetchPage = (page: number, pageSize: number) =>
    AdUnitsApi.getPage({ ...listFilters, page, pageSize, sort })

  const { data, isLoading: loading } = useQuery({
    queryKey: [
      'adUnits',
      scopeKey,
      search,
      statusFilter,
      publisherFilter,
      siteFilter,
      page,
      pageSize,
      sort,
    ],
    queryFn: () => fetchPage(page, pageSize),
    enabled: hasToken && scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const adUnits = data?.results ?? []
  const totalCount = data?.totalCount ?? 0
  const queryClient = useQueryClient()
  const [selection, setSelection] = useState<AdUnit[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [tagDownloadOpen, setTagDownloadOpen] = useState(false)

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((a) => AdUnitsApi.update(a.id, { name: a.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['adUnits'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'adUnits.doneDetail_activate' : 'adUnits.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('adUnits.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('adUnits.title')}</h1>
            <p>{t('adUnits.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <ImportExportButtons
              loadAll={() => AdUnitsApi.getAllPages(listFilters)}
              filename="ad-units"
              idFields={['teams']}
              excludeFields={['siteName', 'earningsAccountId', 'earningsAccountName', 'requestedInLastDay', 'tagId', 'createTime', 'updateTime']}
              fieldTransformers={{ dimensions: dimensionsTransformer }}
              templateFields={['id', 'name', 'objectState', 'labels', 'paywalled', 'dimensions', 'minWidth', 'width', 'minHeight', 'height', 'site', 'filterTeamsType', 'teams']}
              templateExample={{
                id: '',
                name: 'My Ad Unit',
                objectState: 'ACTIVE',
                labels: '',
                paywalled: 'false',
                dimensions: '980x240, 300x250',
                minWidth: '',
                width: '',
                minHeight: '',
                height: '',
                site: 'site-id-here',
                filterTeamsType: 'PRIVATE',
                teams: '',
              }}
              onUpload={async (rows) => {
                const stripKeys = new Set(['siteName', 'earningsAccountId', 'earningsAccountName', 'requestedInLastDay', 'tagId', 'createTime', 'updateTime'])
                const items = rows.map((row) => {
                  const { id, siteId, ...rest } = row as Record<string, unknown>
                  for (const k of stripKeys) delete rest[k]
                  return {
                    id: id as string,
                    ...rest,
                    ...(siteId ? { site: { id: siteId } } : {}),
                  }
                })
                await AdUnitsApi.bulkSave(items as (Parameters<typeof AdUnitsApi.update>[1] & { id: string })[])
                queryClient.invalidateQueries({ queryKey: ['adUnits'] })
              }}
            />
            <CreateObjectButton type="adUnit" size="default" />
          </div>
        </div>

        <ListCard fetchAll={() => walkAllPages(fetchPage)} tone="delivery">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Table} />
                {t('adUnits.title')}
              </>
            }
            count={totalCount}
            actions={<PageSizeSelect value={pageSize} onChange={(size) => refilter(() => setPageSize(size))} />}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                  extraActiveCount={(publisherFilter ? 1 : 0) + (siteFilter ? 1 : 0)}
                  onReset={() =>
                    refilter(() => {
                      setStatusFilter('ACTIVE')
                      setPublisherFilter('')
                      setSiteFilter('')
                    })
                  }
                >
                  <FilterField label={t('sites.publisher')}>
                    <EarningsAccountSelect
                      value={publisherFilter}
                      onChange={(v) =>
                        refilter(() => {
                          setPublisherFilter(v)
                          // A site from another publisher would match nothing.
                          setSiteFilter('')
                        })
                      }
                      anyLabel={t('common.all')}
                      className="h-8"
                    />
                  </FilterField>
                  <FilterField label={t('adUnits.site')}>
                    <SiteSelect
                      value={siteFilter}
                      onChange={(v) => refilter(() => setSiteFilter(v))}
                      earningsAccountId={publisherFilter || undefined}
                      anyLabel={t('common.all')}
                      className="h-8"
                    />
                  </FilterField>
                </StatusFilterButton>
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              { label: t('common.activate'), icon: 'pi pi-check-circle', severity: 'success', onClick: () => bulkSetStatus('ACTIVE') },
              { label: t('common.deactivate'), icon: 'pi pi-ban', severity: 'danger', onClick: () => bulkSetStatus('INACTIVE') },
              { label: t('adUnitTags.action'), icon: 'download', onClick: () => setTagDownloadOpen(true) },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />
          <AdUnitTagDownloadDialog adUnits={selection} open={tagDownloadOpen} onOpenChange={setTagDownloadOpen} />

          <CardContent className="p-0">
            <AdUnitTable
              adUnits={adUnits}
              loading={loading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={
                hasToken ? t('adUnits.noAdUnitsFound') : t('common.signInToLoad')
              }
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('adUnits.adUnitCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
